import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AudioLayout from "../AudioLayout";
import { audioApi, formatErr, track } from "../api";
import { useAudioAuth } from "../AudioAuthContext";
import { useAudioAccess } from "../AudioAccessContext";
import { ArrowLeft, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function AudioVachana() {
  const { vachanaId } = useParams();
  const nav = useNavigate();
  const { user, subscribed } = useAudioAuth();
  const access = useAudioAccess();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const startedAt = useRef(null);
  const sessionMarker = useRef(Math.random().toString(36).slice(2, 12));
  const canPlay = access.is_free || (user && subscribed);

  useEffect(() => {
    if (!canPlay) return;
    audioApi.get(`/vachanas/${vachanaId}/embed`)
      .then(r => {
        setData(r.data);
        startedAt.current = Date.now();
        track("vachana_played", { vachana_id: vachanaId, book_id: r.data.book_id, session_marker: sessionMarker.current });
        track("video_started", { vachana_id: vachanaId, book_id: r.data.book_id, session_marker: sessionMarker.current });
      })
      .catch(e => {
        if (e?.response?.status === 402) { setError("subscribe"); }
        else if (e?.response?.status === 401) { setError("login"); }
        else { setError(formatErr(e)); toast.error(formatErr(e)); }
      });
    return () => {
      if (startedAt.current) {
        const dur = Math.floor((Date.now() - startedAt.current) / 1000);
        track("video_completed", { vachana_id: vachanaId, duration_sec: dur, session_marker: sessionMarker.current });
      }
    };
  }, [vachanaId, canPlay]);

  // PAID mode: need login first
  if (!access.is_free && !user) {
    return (
      <AudioLayout>
        <div className="max-w-md mx-auto text-center py-16" data-testid="ac-vachana-needs-login">
          <Lock className="mx-auto mb-4 text-amber-300" size={28}/>
          <h1 className="text-2xl font-bold">Sign in to listen</h1>
          <p className="text-white/65 mt-2 text-sm">Sign in with Google to play this vachana.</p>
          <Link to={`/audio-comics/login?next=${encodeURIComponent(`/audio-comics/vachana/${vachanaId}`)}`} className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-black font-semibold">Continue with Google</Link>
        </div>
      </AudioLayout>
    );
  }

  // PAID mode: need subscription
  if (!access.is_free && user && !subscribed) {
    return (
      <AudioLayout>
        <div className="max-w-md mx-auto text-center py-16" data-testid="ac-vachana-paywall">
          <Sparkles className="mx-auto mb-4 text-amber-300" size={28}/>
          <h1 className="text-2xl font-bold">{access.cta_secondary}</h1>
          <p className="text-white/65 mt-2 text-sm">
            {access.plan_description} · {access.currency_symbol}{access.subscription_price}/month.
          </p>
          <Link to="/audio-comics/subscription" className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-400 text-black font-semibold">Subscribe now</Link>
          <button onClick={() => nav(-1)} className="block mx-auto mt-4 text-xs text-white/50 hover:text-white">Go back</button>
        </div>
      </AudioLayout>
    );
  }

  if (error && error !== "subscribe" && error !== "login") {
    return (
      <AudioLayout>
        <div className="max-w-md mx-auto text-center py-16">
          <h1 className="text-xl font-bold mb-2">Playback error</h1>
          <p className="text-white/60 text-sm">{error}</p>
          <button onClick={() => window.location.reload()} className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm">Retry</button>
        </div>
      </AudioLayout>
    );
  }

  if (!data) return <AudioLayout><div className="text-center text-white/60 py-16">Loading…</div></AudioLayout>;

  return (
    <AudioLayout>
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-6" data-testid="ac-back-book"><ArrowLeft size={14}/> Back</button>

      <div className="text-[11px] uppercase tracking-[0.25em] text-amber-300 mb-3">Now playing</div>
      <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="ac-vachana-title">{data.title}</h1>
      {data.description && <p className="text-white/70 text-sm mb-5 max-w-2xl">{data.description}</p>}

      <div className="aspect-video rounded-2xl overflow-hidden bg-black ring-1 ring-white/10 max-w-3xl">
        <iframe
          src={data.embed_url}
          title={data.title}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          data-testid="ac-vachana-iframe"
        />
      </div>
    </AudioLayout>
  );
}
